// ── VATReturnHelper.js — 부가세 신고서 미리채움 가이드 ─────────────────────
// 홈택스 일반과세자 신고서 항목별 ddabak 데이터 합계 + "이 칸에 입력하세요" 가이드.

import React, { useState } from 'react';
import { buildVATReturnData } from '../utils/taxExport';

const fmt = (n) => Math.round(Number(n) || 0).toLocaleString('ko-KR');

export default function VATReturnHelper({ transactions = [], periodLabel = '', annualRevenue = null, industry = 'food', onClose }) {
  const data = buildVATReturnData(transactions, { annualRevenue, industry });
  const [copied, setCopied] = useState('');

  const copy = (key, value) => {
    navigator.clipboard?.writeText(String(value)).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(''), 1200);
    });
  };

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    padding: 16, overflow: 'auto',
  };
  const modal = {
    background: '#fff', borderRadius: 14, padding: 0,
    width: 720, maxWidth: '100%',
    boxShadow: '0 8px 40px rgba(0,0,0,0.2)',
    margin: '20px auto',
    overflow: 'hidden',
  };
  const sectionCard = {
    background: '#fff', borderRadius: 8, padding: 14,
    border: '1px solid #e5e7eb', marginBottom: 10,
  };
  const fieldRow = {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 10px', background: '#f9fafb',
    borderRadius: 6, marginBottom: 6,
  };

  const Field = ({ label, value, hint, highlight }) => {
    const k = label;
    const isCopied = copied === k;
    return (
      <div style={{ ...fieldRow, background: highlight ? '#FAFAFA' : '#f9fafb' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{label}</div>
          {hint && <div style={{ fontSize: 10, color: '#888', marginTop: 1 }}>{hint}</div>}
        </div>
        <div style={{ fontSize: 15, fontWeight: 800, color: highlight ? '#EA580C' : '#FF6F0F', minWidth: 110, textAlign: 'right' }}>
          {fmt(value)}원
        </div>
        <button onClick={() => copy(k, value)} type="button"
          style={{
            padding: '5px 10px', fontSize: 11, fontWeight: 600,
            border: '1px solid', borderColor: isCopied ? '#FF6F0F' : '#ddd',
            background: isCopied ? '#FF6F0F' : '#fff',
            color: isCopied ? '#fff' : '#666',
            borderRadius: 5, cursor: 'pointer',
            transition: 'all .12s',
          }}>
          {isCopied ? '✓ 복사됨' : '📋 복사'}
        </button>
      </div>
    );
  };

  return (
    <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}>
      <div style={modal}>
        {/* 헤더 */}
        <div style={{
          padding: '14px 18px', borderBottom: '1px solid #eee',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: '#f9fafb',
        }}>
          <div>
            <div style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>📤 부가세 신고서 미리채움</div>
            <div style={{ fontWeight: 700, fontSize: 15, marginTop: 2 }}>일반과세자 부가가치세 신고 · {periodLabel}</div>
          </div>
          <button onClick={onClose} type="button"
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888' }}>×</button>
        </div>

        {/* 본문 */}
        <div style={{ padding: 18, maxHeight: '70vh', overflow: 'auto' }}>

          {/* 안내 박스 */}
          <div style={{
            padding: 12, marginBottom: 16, borderRadius: 8,
            background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: 12, color: '#1e40af',
          }}>
            💡 <strong>홈택스 부가세 신고서</strong> 화면을 열고, 아래 숫자를 해당 칸에 입력하세요.
            각 항목 옆 <strong>📋 복사</strong> 버튼으로 클립보드에 복사 가능.
            <br/>
            <a href="https://www.hometax.go.kr/" target="_blank" rel="noreferrer"
              style={{ color: '#2563eb', fontWeight: 700, textDecoration: 'underline' }}>
              → 홈택스 바로가기
            </a>
          </div>

          {/* ── 매출세액 ── */}
          <div style={{ marginBottom: 14 }}>
            <SectionHeader label="① 매출세액" />
            <div style={sectionCard}>
              <Field label="세금계산서 발급분 — 공급가액"
                value={data.sales.taxInvoice.supply}
                hint="홈택스 칸: 매출세액 > 세금계산서 발급분 > 금액(1번 칸)" />
              <Field label="세금계산서 발급분 — 세액"
                value={data.sales.taxInvoice.vat}
                hint="동일 칸: 세액(2번 칸)" />
              <Field label="신용카드·현금영수증 발급분 — 공급가액"
                value={data.sales.cardCash.supply}
                hint="홈택스 칸: 신용카드매출전표·현금영수증 발급분 > 금액(3번 칸)" />
              <Field label="신용카드·현금영수증 발급분 — 세액"
                value={data.sales.cardCash.vat}
                hint="동일 칸: 세액" />
              {data.sales.other.supply > 0 && (
                <>
                  <Field label="기타 매출 (간이영수증 등) — 공급가액"
                    value={data.sales.other.supply}
                    hint="홈택스 칸: 기타(4번 칸). 비적격증빙 포함" />
                </>
              )}
              <Field label="매출세액 합계"
                value={data.sales.total.vat}
                hint="자동 계산. 신고서 합계와 일치 확인"
                highlight />
            </div>
          </div>

          {/* ── 매입세액 ── */}
          <div style={{ marginBottom: 14 }}>
            <SectionHeader label="② 매입세액" />
            <div style={sectionCard}>
              <Field label="세금계산서 수취분 — 공급가액"
                value={data.expense.taxInvoice.supply}
                hint="홈택스 칸: 매입세액 > 세금계산서 수취분 > 일반매입(10번 칸)" />
              <Field label="세금계산서 수취분 — 세액"
                value={data.expense.taxInvoice.vat}
                hint="동일 칸: 세액" />
              <Field label="신용카드·현금영수증 수취분 — 공급가액"
                value={data.expense.cardCash.supply}
                hint="홈택스 칸: 그 밖의 공제매입세액 > 신용카드매출전표 등(14번 칸)" />
              <Field label="신용카드·현금영수증 수취분 — 세액"
                value={data.expense.cardCash.vat} />
            </div>
          </div>

          {/* ── 의제매입공제 ── */}
          {data.expense.presumed.supply > 0 && (
            <div style={{ marginBottom: 14 }}>
              <SectionHeader label="③ 의제매입세액공제 ✨" sub="음식점업 면세 농축수산물 매입" />
              <div style={sectionCard}>
                <Field label="의제매입대상 매입가"
                  value={data.expense.presumed.supply}
                  hint="자동 마킹된 면세 농축수산물 매입 합계 (사용자 확인 권장)" />
                <Field label={`의제매입세액공제 추정 (${data.expense.presumed.rate?.numerator || 8}/${data.expense.presumed.rate?.denominator || 108})`}
                  value={data.expense.presumed.deduction}
                  hint={`홈택스 칸: 의제매입세액(16번 칸). 적용율 ${data.expense.presumed.rate?.label || '8/108'}`}
                  highlight />
                <div style={{ fontSize: 11, color: '#888', marginTop: 8, padding: '8px 10px', background: '#F4F4F5', borderRadius: 6 }}>
                  ⚠ 의제매입공제는 매출의 일정 비율 한도 적용. 한도 검증 V1.5에서 자동화.
                  지금은 한도 검토 후 신고하세요.
                </div>
              </div>
            </div>
          )}

          {/* ── 납부세액 ── */}
          <div style={{ marginBottom: 14 }}>
            <SectionHeader label="④ 납부 (또는 환급) 세액" />
            <div style={{
              ...sectionCard,
              background: data.payable >= 0 ? '#fef2f2' : '#FAFAFA',
              border: '2px solid ' + (data.payable >= 0 ? '#fca5a5' : '#FED7AA'),
            }}>
              <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
                매출세액({fmt(data.sales.total.vat)}) − 매입세액({fmt(data.expense.total.deductible)}) − 의제매입({fmt(data.expense.presumed.deduction)})
              </div>
              <div style={{
                fontSize: 28, fontWeight: 800,
                color: data.payable >= 0 ? '#52525B' : '#EA580C',
                textAlign: 'center', marginTop: 8, marginBottom: 4,
              }}>
                {data.payable >= 0 ? '납부' : '환급'} {fmt(Math.abs(data.payable))}원
              </div>
              <div style={{ fontSize: 11, color: '#888', textAlign: 'center' }}>
                ※ 가산세·예정신고분·기타공제 미반영 추정치
              </div>
            </div>
          </div>

          {/* ── 검증 체크리스트 ── */}
          <div style={{ marginBottom: 14 }}>
            <SectionHeader label="⑤ 신고 전 검증 체크리스트" />
            <div style={sectionCard}>
              {[
                '거래처 사업자번호 모두 정확한지 (특히 세금계산서 수취분)',
                '간이영수증 비중이 매입의 5% 넘지 않는지 (가산세 위험)',
                '의제매입공제 매출 한도 안 넘는지 (음식점 35~50%)',
                '사업/개인 분리 — 개인 카드 사용분 빠졌는지',
                '인건비·임차료·공과금 누락 없는지',
                '대출이자·접대비는 매입세액공제 X',
              ].map((t, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, padding: '5px 0', fontSize: 12, color: '#444' }}>
                  <span style={{ color: '#9ca3af' }}>☐</span>
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── 면책 ── */}
          <div style={{ fontSize: 11, color: '#888', textAlign: 'center', padding: '10px 0' }}>
            본 자료는 ddabak이 자동 분류한 결과의 추정치. 최종 신고는 본인 또는 세무사가 검증·확정.
          </div>
        </div>

        {/* 푸터 */}
        <div style={{
          padding: '12px 18px', borderTop: '1px solid #eee',
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          background: '#f9fafb',
        }}>
          <button onClick={onClose} type="button"
            style={{ padding: '7px 14px', borderRadius: 7, border: '1px solid #ddd', background: '#fff', color: '#666', fontSize: 13, cursor: 'pointer' }}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ label, sub }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#18181B' }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: '#888' }}>{sub}</div>}
    </div>
  );
}
